import React, { useState, useEffect, useRef } from "react";
import { ConfirmationModal } from "../../components/checklists/ConfirmationModal";
import { useNavigate } from "react-router-dom";
import {
  useGetChecklistTemplatesQuery,
  useDeleteChecklistTemplateMutation,
  ChecklistTemplate
} from "../../utils/api/endpoints/checklists/checklistApi";
import usePageTitle from "../../utils/hooks/usePageTitle";
import toast from "react-hot-toast";
import { 
  FiPlus, FiSearch, FiEdit2, FiEye, FiTrash2, FiCheckCircle, 
  FiXCircle, FiLayers, FiTag, FiFileText, FiTarget, FiBox, 
  FiLayout, FiCheck, FiMoreVertical 
} from "react-icons/fi";

const ChecklistTemplatesPage: React.FC = () => {
  usePageTitle("Checklist Templates");
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  const { data, isLoading, error, refetch } = useGetChecklistTemplatesQuery({
    active_only: showActiveOnly
  });

  const [deleteTemplate] = useDeleteChecklistTemplateMutation();

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type: "info" | "danger" | "warning" | "success";
    confirmLabel?: string;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    type: "info",
  });

  const handleDelete = async (templateId: number, templateName: string) => {
    setConfirmModal({
      isOpen: true,
      title: "Delete Template",
      message: `Are you sure you want to delete the template "${templateName}"? This action cannot be undone.`,
      type: "danger",
      confirmLabel: "Delete",
      onConfirm: async () => {
        try {
          await deleteTemplate(templateId).unwrap();
          toast.success("Template deleted successfully");
          refetch();
        } catch (err: any) {
          toast.error(err?.data?.message || "Failed to delete template");
        }
      }
    });
  };

  const filteredTemplates = data?.data?.filter((template: ChecklistTemplate) =>
    template.template_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="montserrat-font flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
          <p className="text-xl font-bold text-gray-900">Loading Templates</p>
          <p className="mt-2 text-sm text-gray-600">Please wait...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="montserrat-font flex min-h-screen items-center justify-center bg-slate-50">
        <div className="max-w-md rounded-2xl bg-white p-8 text-center shadow-2xl">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="mb-2 text-xl font-bold text-gray-800">Error Loading Templates</h3>
          <p className="mb-4 text-gray-600">We couldn't load the templates. Please try again.</p>
          <button
            onClick={() => window.location.reload()}
            className="rounded-lg bg-blue-600 px-6 py-2.5 font-semibold text-white transition-all hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const totalTestCases = data?.data?.reduce((sum: number, t: ChecklistTemplate) => sum + (t.items?.length || 0), 0) || 0;
  const activeCount = data?.data?.filter((t: ChecklistTemplate) => t.is_active).length || 0;

  return (
    <div className="montserrat-font min-h-screen bg-slate-50/50">
      <div className="mx-auto max-w-7xl px-6 py-10">
        {/* Modern Action Header - Refined Minimal Style */}
        <div className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-6 px-2">
          <div className="flex items-center gap-6">
            <div 
              className="flex h-14 w-14 items-center justify-center rounded-xl border border-[#58CC02]/10 shadow-sm"
              style={{ backgroundColor: 'rgba(88, 204, 2, 0.17)' }}
            >
              <FiLayout className="h-7 w-7 text-[#58CC02]" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#010D2C] tracking-tight">Checklist Templates</h1>
              <p className="text-slate-400 font-semibold text-[11px] tracking-widest mt-0.5 uppercase">
                Manage security testing frameworks
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/dashboard/checklist-review")}
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-5 py-2.5 text-[11px] font-bold uppercase tracking-widest text-[#010D2C] shadow-sm transition-all hover:bg-slate-50"
            >
              <FiFileText className="h-4 w-4" />
              Checklist Review
            </button>
            <button
              onClick={() => navigate("/dashboard/checklist-templates/create")}
              className="flex items-center gap-2 rounded-lg bg-[#010D2C] px-6 py-2.5 text-[11px] font-bold uppercase tracking-widest text-[#58CC02] shadow-md transition-all hover:scale-[1.01]"
            >
              <FiPlus className="h-4 w-4" />
              Create Template
            </button>
          </div>
        </div>

        {/* Navy Statistics Bar - Refined Geometry & Typography */}
        <div className="mb-8 rounded-xl bg-[#010D2C] p-6 shadow-xl border border-slate-800">
           <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/5">
              <div className="flex items-center gap-5 px-4 py-2 md:py-0">
                 <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-800/40 text-[#58CC02]">
                    <FiLayers className="h-5 w-5 opacity-90" />
                 </div>
                 <div>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-[0.15em] mb-1">Total Templates</p>
                    <p className="text-xl font-bold text-white tracking-tight">{data?.data?.length || 0}</p>
                 </div>
              </div>
              <div className="flex items-center gap-5 px-10 py-6 md:py-0">
                 <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-800/40 text-[#58CC02]">
                    <FiCheckCircle className="h-5 w-5 opacity-90" />
                 </div>
                 <div>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-[0.15em] mb-1">Active Templates</p>
                    <p className="text-xl font-bold text-white tracking-tight">{activeCount}</p>
                 </div>
              </div>
              <div className="flex items-center gap-5 px-10 py-6 md:py-0">
                 <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-800/40 text-[#58CC02]">
                    <FiTag className="h-5 w-5 opacity-90" />
                 </div>
                 <div>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-[0.15em] mb-1">Total Test Cases</p>
                    <p className="text-xl font-bold text-white tracking-tight">{totalTestCases}</p>
                 </div>
              </div>
           </div>
        </div>

        {/* Refined Filter Bar - Separated Containers */}
        <div className="mb-8 flex flex-col md:flex-row items-center gap-4">
          <div className="relative flex-1 group bg-white rounded-lg border border-slate-200 shadow-sm transition-all focus-within:border-slate-300">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#010D2C] transition-colors" />
            <input
              type="text"
              placeholder="Search by template name..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border-none bg-transparent py-3.5 pl-12 pr-4 text-[13px] font-bold text-slate-900 placeholder-slate-400 focus:ring-0 transition-all"
            />
          </div>
          
          <button
            onClick={() => setShowActiveOnly(!showActiveOnly)}
            className={`flex items-center gap-4 rounded-lg bg-white border border-slate-200 px-6 py-3.5 shadow-sm transition-all hover:bg-slate-50 ${
              showActiveOnly ? 'border-emerald-200' : ''
            }`}
          >
            <div className={`flex h-4 w-4 items-center justify-center rounded border transition-all ${
              showActiveOnly ? 'bg-emerald-500 border-emerald-600 text-white' : 'bg-white border-slate-300 text-transparent'
            }`}>
              <FiCheck className="h-2.5 w-2.5" />
            </div>
            <span className={`text-[11px] font-bold uppercase tracking-widest ${
              showActiveOnly ? 'text-emerald-600' : 'text-slate-500'
            }`}>
              Show Active Only
            </span>
          </button>
        </div>

        {/* Unified Template List Container - Image 2 Style */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          {filteredTemplates && filteredTemplates.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {filteredTemplates.map((template: ChecklistTemplate) => (
                <div
                  key={template.template_id}
                  className="group relative flex items-start gap-6 p-6 transition-all hover:bg-slate-50/50"
                >
                  {/* Leading Icon - Image 2 Small Square Style */}
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#010D2C] shadow-sm">
                    <FiLayers className="h-5 w-5 text-white" />
                  </div>

                  {/* Main Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <h3 className="text-[15px] font-semibold text-[#010D2C] tracking-tight truncate">
                        {template.template_name}
                      </h3>
                      {template.version && (
                        <span className="px-2 py-0.5 rounded-md text-[9px] font-semibold bg-slate-100 text-slate-500 border border-slate-200">
                          v{template.version}
                        </span>
                      )}
                      <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[9px] font-semibold border ${
                        template.is_active 
                          ? 'bg-emerald-50 text-emerald-600 border-emerald-100' 
                          : 'bg-orange-50 text-orange-600 border-orange-100'
                      }`}>
                        <div className={`h-1.5 w-1.5 rounded-full ${template.is_active ? 'bg-emerald-500' : 'bg-orange-400'}`}></div>
                        {template.is_active ? 'Active' : 'Draft'}
                      </div>
                    </div>

                    <p className="text-[12px] font-medium text-slate-400 leading-relaxed mb-4 line-clamp-1 max-w-5xl">
                      {template.description || "Standardized security controls and verification objectives for comprehensive application assessment."}
                    </p>

                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2 text-slate-400">
                        <FiTag className="h-3.5 w-3.5" />
                        <span className="text-[10px] font-semibold uppercase tracking-widest leading-none mt-0.5">{template.items?.length || 0} Test Cases</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400">
                        <div className="h-4 w-4 rounded-full bg-blue-600 flex items-center justify-center">
                          <FiTarget className="h-2 w-2 text-white" />
                        </div>
                        <span className="text-[10px] font-semibold uppercase tracking-widest leading-none text-blue-600 mt-0.5">{template.targetMappings?.length || 0} Targets</span>
                      </div>
                      
                      {/* Platform Tags Cluster */}
                      <div className="flex items-center gap-2">
                        {template.targetMappings?.slice(0, 3).map(mapping => (
                          <span 
                            key={mapping.mapping_id} 
                            className="px-2 py-0.5 rounded bg-slate-50 border border-slate-200 text-[9px] font-semibold text-slate-400"
                          >
                            {mapping.target_type}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Actions Column - Image 2 Simple Icon Style */}
                  <div className="flex items-center gap-1 self-center ml-4">
                    <button
                      onClick={() => navigate(`/dashboard/checklist-templates/${template.template_id}/edit`)}
                      className="flex h-9 w-9 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-[#010D2C] transition-all"
                      title="Edit"
                    >
                      <FiEdit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => navigate(`/dashboard/checklist-templates/${template.template_id}/view`)}
                      className="flex h-9 w-9 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-[#010D2C] transition-all"
                      title="View"
                    >
                      <FiEye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(template.template_id, template.template_name)}
                      className="flex h-9 w-9 items-center justify-center rounded-md text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all"
                      title="Delete"
                    >
                      <FiTrash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-20 text-center">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-slate-50 border border-slate-100">
                <FiLayout className="h-10 w-10 text-slate-200" />
              </div>
              <h3 className="mb-2 text-xl font-bold text-[#010D2C] tracking-tight">
                {searchTerm ? "No Match Found" : "No Templates Available"}
              </h3>
              <p className="mx-auto text-sm font-medium text-slate-400 leading-relaxed max-w-md">
                {searchTerm
                  ? "Try refining your search parameters or explore the full catalog."
                  : "Initiate your security framework by creating your first template."}
              </p>
            </div>
          )}
        </div>
      </div>
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        type={confirmModal.type}
      />
    </div>
  );
};

export default ChecklistTemplatesPage;

